import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  InputAdornment,
  Modal,
  TextField,
} from '@material-ui/core';
import { AddCircle, DeleteForever } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import CustomTable from '../components/CustomTable';
import ActionButton from '../../../components/ActionButton';
import CustomSnackbar from '../../../components/CustomSnackbar';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles((theme) => ({
  cell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  conformitaWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%',
    maxWidth: 700,
  },
  rowWrapper: {
    display: 'grid',
    gridTemplateColumns: '130px 1fr',
    alignItems: 'center',
    columnGap: theme.spacing(3),
    width: '100%',
  },
  conformitaLabel: {
    justifySelf: 'end',
  },
  textField: {
    width: '90%',
    margin: 10,
    '& .MuiInputBase-input': {
      textAlign: 'center', // Centra il testo all'interno del TextField
    },
  },
  iconButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[6],
    borderRadius: '6px',
    padding: theme.spacing(3),
    width: '90%',
    maxWidth: '960px',

    overflowY: 'auto',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  inputWrapper: {
    width: '100%',
    maxWidth: '600px',
    '& .MuiInputBase-input': {
      textAlign: 'center',
    },
  },
  tableWrapper: {
    width: '85%',
    overflowX: 'auto',
  },
}));

const AggiuntaOpzioniColonnaModal = ({
  columnData,
  deleteOpzioneRow,
  onClose,
  open,
  handleSaveNewOpzioni,
  selectedIndex,
}) => {
  const classes = useStyles();

  const [newOpzione, setNewOpzione] = useState('');
  const [opzioniList, setOpzioniList] = useState(
    columnData[selectedIndex]?.opzioni || [],
  );

  const [snackBar, setSnackBar] = useState({
    open: false,
    message: '',
  });

  const showSnackbar = (message) => {
    setSnackBar({ open: true, message });
  };

  const handleAddOpzione = () => {
    if (newOpzione.trim() !== '') {
      setOpzioniList((prev) => [...prev, newOpzione]);
      setNewOpzione('');
    }
  };

  const handleValueChange = (value, rowIndex) => {
    setOpzioniList((prev) =>
      prev.map((opzione, i) => (i === rowIndex ? value : opzione)),
    );
  };

  const onDelete = (rowIndex) => {
    setOpzioniList((prev) => prev.filter((_, i) => i !== rowIndex));
    deleteOpzioneRow(rowIndex);
  };

  const isConformita = columnData[selectedIndex]?.tipo === 'conformita';

  const onSave = () => {
    if (isConformita) {
      if (!opzioniList[0]?.trim() || !opzioniList[1]?.trim()) {
        showSnackbar('Entrambi i valori di conformità devono essere inseriti.');
        return;
      }
      handleSaveNewOpzioni([opzioniList[0].trim(), opzioniList[1].trim()]);
      onClose();
      return;
    }
    const hasEmptyValues = opzioniList.some((value) => value.trim() === '');
    if (hasEmptyValues) {
      showSnackbar('I valori aggiunti non devono essere vuoti.');
    } else {
      handleSaveNewOpzioni(opzioniList);
      onClose();
    }
  };

  const columns = [
    {
      Header: 'Valore',
      id: 'opzione',
      minWidth: 540,
      accessor: (d) => d,
      Cell: (props) => {
        const [localValue, setLocalValue] = useState(
          opzioniList[props.row._index] || '',
        );

        return (
          <div className={classes.cell}>
            <TextField
              className={classes.textField}
              defaultValue={localValue}
              onChange={(event) => setLocalValue(event.target.value)}
              onBlur={() => handleValueChange(localValue, props.row._index)}
            />
          </div>
        );
      },
    },
    {
      Header: 'Elimina',
      id: 'elimina',
      width: 180,
      accessor: (d) => d,
      Cell: (props) => (
        <div className={classes.iconButton}>
          <IconButton
            title="Elimina"
            onClick={() => onDelete(props.row._index)}
          >
            <DeleteForever color="primary" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <Modal open={open} onClose={onClose} className={classes.modal}>
      <div className={classes.paper}>
        {isConformita ? (
          <div className={classes.conformitaWrapper}>
            <Typography variant="h6" gutterBottom>
              Aggiungi valori
            </Typography>
            <div className={classes.rowWrapper}>
              <Typography className={classes.conformitaLabel}>
                Conforme:
              </Typography>
              <TextField
                value={opzioniList[0] || ''}
                onChange={(e) => {
                  const arr = [...opzioniList];
                  arr[0] = e.target.value;
                  setOpzioniList(arr);
                }}
                inputProps={{ style: { textAlign: 'center' } }}
                margin="dense"
              />
            </div>
            <div className={classes.rowWrapper}>
              <Typography className={classes.conformitaLabel}>
                Non conforme:
              </Typography>
              <TextField
                value={opzioniList[1] || ''}
                onChange={(e) => {
                  const arr = [...opzioniList];
                  arr[1] = e.target.value;
                  setOpzioniList(arr);
                }}
                inputProps={{ style: { textAlign: 'center' } }}
                margin="dense"
              />
            </div>
          </div>
        ) : (
          <>
            <TextField
              id="nuova-opzione-valore"
              className={classes.inputWrapper}
              label="Inserisci un nuovo valore"
              value={newOpzione}
              onChange={(e) => setNewOpzione(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton title="Aggiungi" onClick={handleAddOpzione}>
                      <AddCircle color="primary" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div className={classes.tableWrapper}>
              <CustomTable
                records={opzioniList}
                columns={columns}
                loading={false}
                filterable={false}
                resizable={true}
                sortable={false}
                defaultPageSize={10}
                tableStyle={{
                  height: 400,
                }}
              />
            </div>
          </>
        )}

        <div>
          <ActionButton label="Chiudi" secondaryButton onClick={onClose} />
          <ActionButton label="Salva" onClick={onSave} />
        </div>
        <CustomSnackbar
          open={snackBar.open}
          onClose={() => setSnackBar({ open: false, message: '' })}
          message={snackBar.message}
          severity="error"
        />
      </div>
    </Modal>
  );
};

AggiuntaOpzioniColonnaModal.propTypes = {
  columnData: PropTypes.array.isRequired,
  deleteOpzioneRow: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  handleSaveNewOpzioni: PropTypes.func.isRequired,
  selectedIndex: PropTypes.number.isRequired,
};

export default AggiuntaOpzioniColonnaModal;
